import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Twitter, Youtube, Shield, RefreshCw } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    'Quick Links': [
      { name: 'Home', path: '/' },
      { name: 'Gold Collection', path: '/gold' },
      { name: 'Silver Collection', path: '/silver' },
      { name: 'Savings Schemes', path: '/savings-schemes' },
    ],
    'Services': [
      { name: 'Gold Savings', path: '/savings-schemes' },
      { name: 'Silver Savings', path: '/savings-schemes' },
      { name: 'Jewellery Exchange', path: '/offers' },
      { name: 'Custom Design', path: '/contact' },
    ],
    'Support': [
      { name: 'Contact Us', path: '/contact' },
      { name: 'Terms & Conditions', path: '/terms' },
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'FAQs', path: '/contact' },
    ],
    'Policies': [
      { name: 'Cancellation Policy', path: '/cancellation-policy' },
      { name: 'Refund Policy', path: '/refund-policy' },
      { name: 'Return Policy', path: '/return-policy' },
      { name: 'Shipping Policy', path: '/shipping-policy' },
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Youtube, href: '#', label: 'YouTube' },
  ];

  return (
    <footer className="bg-[#1a2a39] text-white">
      {/* Main footer content */}
      <div className="container-custom px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-16 h-12 flex items-center justify-center">
                <img 
                  src="/akila-logo.png" 
                  alt="Akila Jewellers Logo" 
                  className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    // Fallback to text logo if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-12 h-12 bg-gold-gradient rounded-lg flex items-center justify-center hidden">
                  <span className="text-white font-playfair font-bold text-xl">A</span>
                </div>
              </div>
              <div>
                {/* <p className="text-sm text-gray-300">Since 1985</p> */}
              </div>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Where tradition meets elegance. We provide the finest quality gold and silver jewellery 
              with unmatched craftsmanship and trust.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 bg-gray-700 hover:bg-gold rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
            
            {/* Mobile App Download */}
            <div className="mt-6">
              <h5 className="text-sm font-semibold text-gold mb-3">Download Our App</h5>
              <a 
                href="https://play.google.com/store/apps/details?id=com.nexooai.akilajewellers"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <span className="text-sm font-medium">Get it on Google Play</span>
              </a>
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-lg font-semibold text-gold mb-6 font-playfair flex items-center">
                {category === 'Policies' && <Shield className="w-5 h-5 mr-2" />}
                {category === 'Support' && <RefreshCw className="w-5 h-5 mr-2" />}
                {category}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-gray-300 hover:text-gold transition-colors duration-300 text-sm flex items-center"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact Info Section */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Phone</p>
                <p className="font-semibold">+91 99761 57984</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Email</p>
                <p className="font-semibold">info@akilajewellers.com</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Address</p>
                <p className="font-semibold">98, Church Building Main Road, Dindigul, TN-624001</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-xs">GST</span>
              </div>
              <div>
                <p className="text-sm text-gray-400">GSTIN</p>
                <p className="font-semibold">33CVKPS4171B1ZN</p>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="text-center">
            <h4 className="text-lg font-semibold text-gold mb-4 font-playfair">Stay Updated</h4>
            <p className="text-gray-300 mb-6">Subscribe to our newsletter for exclusive offers and updates</p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-gold transition-colors duration-300"
              />
              <button className="btn-primary whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800 bg-gray-950">
        <div className="container-custom px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between text-sm text-gray-400">
            <p>&copy; {currentYear} Akila Jewellers. All rights reserved.</p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <Link to="/terms" className="hover:text-gold transition-colors duration-300">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="hover:text-gold transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/cancellation-policy" className="hover:text-gold transition-colors duration-300">
                Cancellation Policy
              </Link>
              <Link to="/refund-policy" className="hover:text-gold transition-colors duration-300">
                Refund Policy
              </Link>
            </div>
          </div>
          
          {/* Developer Credit */}
          <div className="mt-4 pt-4 border-t border-gray-800 text-center">
            <p className="text-xs text-gray-500">
              Developed by{' '}
              <a 
                href="https://agnisofterp.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold hover:text-gold-light transition-colors duration-300 hover:underline"
              >
                @agnisofterp.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
